/**
 * Pure decisions about the "Remember me" preference, kept out of the provider so
 * they can be tested directly (same pattern as `session-state.utils`).
 */

/**
 * Should this cold start throw the stored session away?
 *
 * "Remember me" is the promise that closing the browser / killing the app does
 * NOT sign you out. The session itself is an HttpOnly cookie we cannot scope, so
 * the client enforces the other half: a marker that lives exactly as long as one
 * browser session (a session cookie) or one app run (a module variable). If that
 * marker is gone we are on a fresh run, and a session the user explicitly asked
 * NOT to be remembered must not survive into it.
 *
 * `rememberMe` is tri-state on purpose:
 *   `true`  — remembered; survive the restart.
 *   `false` — this client logged in and the toggle was off; discard.
 *   `null`  — no login has ever been recorded on this device (or logout cleared
 *             it). There is nothing of ours to discard, so don't fire a revoke
 *             on every cold start of a signed-out app.
 */
export const shouldEndUnrememberedSession = ({
  sessionAlive,
  rememberMe,
}: {
  sessionAlive: boolean;
  rememberMe: boolean | null;
}): boolean => rememberMe === false && !sessionAlive;
